import { PickedFile } from '@capawesome/capacitor-file-picker';
import { base64toBlob, blobToBase64, resizeBase64, resizeImage } from '../utils/data';
import { authFetch } from '../service/AuthService';
import { ContractDBPrefix, UserDoc } from 'common';

/*
export interface UserDoc {
  _id: string;
  _rev: string;
  type: string;
  first_name: string;
  last_name: string;
  company_role: string;
  company: string;
  roles: string[];
  projects: string[];
  email: string;
  email_verified: boolean;
  profile: any;
  isGuest?: boolean;
  guestInformation?: { projectId: string, sharedLinkId: string, url: string };
  color: string;
  phone: string;
  password_scheme:string;
  _attachments: { [key: string]: { content_type: string, revpos?: number, digest?: string, length?: number, data: string  } };
}
*/

export class PermissionsSet {
  private roles: string[];
  private projectId: string;
  private _readRole: string;
  private _writeRole: string;
  private _contractId: string;
  private _dbPrefix: ContractDBPrefix | null;

  constructor(roles: string[], projectId: string, readRole: string, writeRole: string, contractId: string = null, dbPrefix: ContractDBPrefix | null = null) {
    this.roles = roles;
    this.projectId = projectId;
    this._readRole = readRole;
    this._writeRole = writeRole;
    this._contractId = contractId;
    this._dbPrefix = dbPrefix;
  }

  get canReadProject(): boolean {
    return this.roles.includes(`p-${this.projectId}`);
  }
  get canWriteProject(): boolean {
    return this.roles.includes(`p-${this.projectId}-write`);
  }

  get writeProjectRole(): string {
    return `p-${this.projectId}-write`;
  }

  get readRole(): string {
    return this._readRole;
  }
  get writeRole(): string {
    return this._writeRole;
  }

  get canRead() {
    return this.canWrite || this.roles.includes(this._readRole) || this.canReadProject;
  }
  get canWrite() {
    return this.roles.includes(this._writeRole) || this.canWriteProject;
  }

  get canWriteSpecificContract() {
    if (!this._contractId) return false;

    if (!this._dbPrefix) {
      throw new Error(`dbPrefix must be set`);
    }

    return this.roles.includes(`p-${this.projectId}-${this._dbPrefix}-${this._contractId}-write`);
  }
}

export class ProjectPermissions {
  private roles: string[];
  private projectId: string;
  constructor(roles: string[], projectId: string) {
    this.roles = roles;
    this.projectId = projectId;
  }
  get canReadProject(): boolean {
    return this.roles.includes(`p-${this.projectId}`);
  }
  get canWriteProject(): boolean {
    return this.roles.includes(`p-${this.projectId}-write`);
  }

  get peoplePermissions() {
    return new PermissionsSet(this.roles, this.projectId, `p-${this.projectId}-people`, `p-${this.projectId}-rfis-people`);
  }
  get rfiPermissions() {
    return new PermissionsSet(this.roles, this.projectId, `p-${this.projectId}-rfis`, `p-${this.projectId}-rfis-write`);
  }
  get scrapbookPermissions() {
    return new PermissionsSet(this.roles, this.projectId, `p-${this.projectId}-schedules`, `p-${this.projectId}-schedules-write`);
  }
  get drawingsPermissions() {
    return new PermissionsSet(this.roles, this.projectId, `p-${this.projectId}-drawings`, `p-${this.projectId}-drawings-write`);
  }
  get submittalsPermissions() {
    return new PermissionsSet(this.roles, this.projectId, `p-${this.projectId}-submittals`, `p-${this.projectId}-submittals-write`);
  }

  getSingleRfiPermissions(rfiId: string): PermissionsSet {
    return new PermissionsSet(this.roles, this.projectId, `p-${this.projectId}-rfi-${rfiId}`, `p-${this.projectId}-rfi-${rfiId}-write`);
  }
}

export class User {
  static readonly COLORS = [
    '#AD1457',
    '#6A1B9A',
    '#4527A0',
    '#1565C0',
    '#283593',
    '#0277BD',
    '#00838F',
    '#00695C',
    '#2E7D32',
    '#558B2F',
    // '#9E9D24',
    '#EF6C00',
    '#D84315',
    '#4E342E',
    '#D8FF00',
    '#4E002E',
  ];

  static async getUserDoc(userId: string): Promise<UserDoc> {
    const response = await authFetch(globalThis.ccLambdaBaseUrl + '/fn/auth/get-user?userId=' + userId, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + globalThis.user.token,
      },
    });

    if (!response.ok) {
      throw new Error((await response.json()).message);
    }

    return await response.json();
  }
  /*
  static async confirmAccessCode(code: string, password: string): Promise<UserSession> {
    const response = await authFetch(globalThis.ccLambdaBaseUrl + '/fn/auth/confirm-access-code', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + globalThis.user.token,
      },
      body: JSON.stringify({ code, password }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message);
    return data;
  }


  static async changePasswordFromCode(email: string, code: string, password: string): Promise<UserSession> {
    const response = await authFetch(globalThis.ccLambdaBaseUrl + '/fn/auth/change-password-from-code', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + (!!globalThis.user ? globalThis.user.token : ''),
      },
      body: JSON.stringify({ email, code, password }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message);
    return data;
  }

  static async updateUser(doc: UserDoc): Promise<UserSession> {
    const response = await authFetch(globalThis.ccLambdaBaseUrl + '/fn/auth/update-user', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + globalThis.user.token,
      },
      body: JSON.stringify({ formData: doc }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error('Error updating user: ' + data.message);
    return data;
  }
    */

  public doc: UserDoc;
  private _pictureUrl: string;

  constructor(doc: UserDoc) {
    this.doc = doc;
  }

  get isAnonymous() {
    return !this.doc.email;
  }

  get pendingEmailConfirmation() {
    return this.doc.email && !this.doc.email_verified;
  }

  get isGuestxx() {
    return this.doc.email && !this.doc.email_verified;
  }

  get hasPassword() {
    return this.doc.has_password;
  }

  get isUser() {
    return this.doc.type == 'user';
  }

  get id() {
    return this.doc._id;
  }

  get roles(): string[] {
    return this.doc.roles;
  }

  private sessionColor: string;
  get color() {
    if (!this.sessionColor) {
      const color = User.COLORS[Math.floor(Math.random() * User.COLORS.length)];
      this.sessionColor = color;
    }
    return this.doc.color || this.sessionColor;
  }

  getProjectPermissions(projectId: string): ProjectPermissions {
    return new ProjectPermissions(this.doc.roles, projectId);
  }

  getSchedulePermissions(projectId: string): PermissionsSet {
    return new PermissionsSet(this.doc.roles, projectId, `p-${projectId}-schedules`, `p-${projectId}-schedules-write`);
  }

  getHoursTypePermissions(projectId: string): PermissionsSet {
    return new PermissionsSet(this.doc.roles, projectId, `p-${projectId}-type`, `p-${projectId}-type-write`);
  }

  getHoursLogPermissions(projectId: string): PermissionsSet {
    return new PermissionsSet(this.doc.roles, projectId, `p-${projectId}-log`, `p-${projectId}-log-write`);
  }

  getDigestPermissions(projectId: string): PermissionsSet {
    return new PermissionsSet(this.doc.roles, projectId, '', `p-${projectId}-sign-digest`);
  }

  getDrawingsPermissions(projectId: string): PermissionsSet {
    return new PermissionsSet(this.doc.roles, projectId, `p-${projectId}-drawings`, `p-${projectId}-drawings-write`);
  }

  getScrapbookPermissions(projectId: string): PermissionsSet {
    return new PermissionsSet(this.doc.roles, projectId, `p-${projectId}-scrapbooks`, `p-${projectId}-scrapbooks-write`);
  }

  getRfiPermissions(projectId: string): PermissionsSet {
    return new PermissionsSet(this.doc.roles, projectId, `p-${projectId}-rfis`, `p-${projectId}-rfis-write`);
  }

  getSingleRfiPermissions(projectId: string, rfiId: string): PermissionsSet {
    return new PermissionsSet(this.doc.roles, projectId, `p-${projectId}-rfi-${rfiId}`, `p-${projectId}-rfi-${rfiId}-write`);
  }

  getSubmittalsPermissions(projectId: string): PermissionsSet {
    return new PermissionsSet(this.doc.roles, projectId, `p-${projectId}-submittals`, `p-${projectId}-submittals-write`);
  }

  getContractPermissions(projectId: string, dbPrefix: ContractDBPrefix): PermissionsSet {
    return new PermissionsSet(this.doc.roles, projectId, `p-${projectId}-${dbPrefix}`, `p-${projectId}-${dbPrefix}-write`, null, dbPrefix);
  }

  getIndividualContractPermissions(projectId: string, contractId: string, dbPrefix: ContractDBPrefix): PermissionsSet {
    return new PermissionsSet(this.doc.roles, projectId, `p-${projectId}-${dbPrefix}-${contractId}`, `p-${projectId}-${dbPrefix}-${contractId}-write`, contractId, dbPrefix);
  }

  hasRole(role: string) {
    return this.doc.roles.includes(role);
  }

  hasOneOfRoles(roles: string[]): boolean {
    return this.doc.roles.some(role => roles.includes(role));
  }

  canAdministerProject(projectId: string) {
    return this.doc.roles.includes(`p-${projectId}-write`);
  }

  canDoBasicThings(projectId: string) {
    return this.canAdministerProject(projectId) || this.doc.roles.includes(`p-${projectId}-base`);
  }

  canAdministerPeople(projectId: string) {
    return this.doc.roles.includes(`p-${projectId}-write`) || this.doc.roles.includes(`p-${projectId}-people-write`);
  }

  get projectIds(): string[] {
    if (!this.doc.projects || this.doc.projects.length == 0) return ['demo'];
    return [...new Set(this.doc.projects)];
  }

  get initials() {
    const fname = this.doc.first_name;
    const lname = this.doc.last_name;

    if (!fname) return null;

    let firstLetter: string = fname ? fname.charAt(0) : '';
    let secondLetter: string = lname ? lname.charAt(0) : fname.charAt(1);
    return String(firstLetter + secondLetter).toUpperCase();
  }

  get fullName() {
    return this.doc.first_name ? this.doc.first_name + ' ' + this.doc.last_name : null;
  }

  get pictureUrl(): string {
    if (this._pictureUrl) return this._pictureUrl;
    if (this.doc.image && this.doc.image.data) return `data:${this.doc.image.mimeType};base64,${this.doc.image.data}`;
    if (!this.doc._attachments || !this.doc._attachments.hasOwnProperty('profilePicture') || !this.doc._attachments['profilePicture'].data) return null;

    const attachment = this.doc._attachments['profilePicture'];
    const contentType = attachment.content_type;
    const blob = base64toBlob(attachment.data, contentType);
    this._pictureUrl = URL.createObjectURL(blob);
    return this._pictureUrl;
  }

  async setSelectedFile(value: PickedFile) {
    if (value) {
      const blob = await resizeImage(value.blob, 86, 86, 'image/webp');
      const base64 = await blobToBase64(blob);
      this.doc._attachments = this.doc._attachments || {};
      this.doc._attachments['profilePicture'] = { content_type: blob.type, data: base64 };
    }
    this._pictureUrl = null;
  }

  async setProfilePictureFromFile(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        this.doc.image = await resizeBase64(reader.result as string, 86, 86);
        resolve();
      };
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    });
  }
}
